<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { camelize } from 'humps';
import { useQuery, useMutation, useQueryClient } from '@tanstack/vue-query';
import { useForm } from 'vee-validate';
import { object, string } from 'yup';
import {
  taxSettingsApi, type TaxSettings, type TaxProfileChoices, type EnrolmentForm,
} from '@/api/tax-settings';
import { apiErrorCode, apiErrorText } from '@/utils/api-errors';
import SectionHeader from '@/components/section-header.vue';
import InfoField from '@/components/info-field.vue';

// "Facturación electrónica": the company connects itself to the e-invoicing
// provider (its signer's authority credentials travel once and are not kept)
// and chooses how it issues. Issuing switches on with the connection.
//
// Which provider Kuido issues through is never named here, nor anywhere else in
// the browser: it is Kuido's arrangement, chosen per account in the
// administration, and a company reading "tupana" on its own settings page
// learns something it has no use for.
const { t } = useI18n();
const queryClient = useQueryClient();

const { data: settings, isLoading } = useQuery({
  queryKey: ['taxSettings'],
  queryFn: taxSettingsApi.show,
});

const successMessage = ref('');
const errorMessage = ref('');
const successSnackbar = ref(false);
const errorSnackbar = ref(false);

function notify(message: string) {
  successMessage.value = message;
  successSnackbar.value = true;
}

function report(error: unknown, fallbackKey: string) {
  errorMessage.value = apiErrorText(error, t(fallbackKey));
  errorSnackbar.value = true;
}

function replaceSettings(fresh: TaxSettings) {
  queryClient.setQueryData(['taxSettings'], fresh);
}

// ---- Profile choices
const profileForm = ref<TaxProfileChoices>({
  taxCategory: 'standard', pricesIncludeTax: true, documentKind: 'invoice',
});

watch(settings, (current) => {
  if (!current) return;
  const { taxCategory, pricesIncludeTax, documentKind } = current.profile;
  profileForm.value = { taxCategory, pricesIncludeTax, documentKind };
}, { immediate: true });

const taxCategoryOptions = computed(() => (settings.value?.profile.taxCategories ?? [])
  .map((category) => ({ value: category, title: t(`taxSettings.taxCategories.${category}`) })));
const documentKindOptions = computed(() => (settings.value?.profile.documentKinds ?? [])
  .map((kind) => ({ value: kind, title: t(`taxSettings.documentKinds.${kind}`) })));

const { mutateAsync: saveProfile, isPending: saving } = useMutation({
  mutationFn: (choices: TaxProfileChoices) => taxSettingsApi.update(choices),
  onSuccess: (fresh) => {
    replaceSettings(fresh);
    notify(t('taxSettings.saved'));
  },
  onError: (error) => report(error, 'taxSettings.saveError'),
});

// ---- Connection
const account = computed(() => settings.value?.account ?? null);
const connected = computed(() => !!account.value?.connected);
const connectionChip = computed(() => {
  if (!account.value?.connected) return { color: 'grey', label: t('taxSettings.status.disconnected') };
  if (account.value.credentialStatus === 'valid') {
    return { color: 'success', label: t('taxSettings.status.connected') };
  }

  return { color: 'error', label: t(`taxSettings.status.${account.value.credentialStatus ?? 'invalid'}`) };
});
const webhookNotice = computed(() => {
  const webhook = account.value?.webhook;
  if (!webhook || webhook.status === 'registered') return '';

  return t(`taxSettings.webhook.${camelize(webhook.status)}`, { error: webhook.error ?? '' });
});

// The readiness the profile already computes per kind, which the form used to
// receive and discard: a company whose certificate is missing looks perfectly
// connected until an emission fails.
const READINESS_KEYS: Record<string, string> = {
  'true,true': 'both', 'true,false': 'invoiceOnly', 'false,true': 'receiptOnly', 'false,false': 'none',
};

const readinessKey = computed(() => {
  const ready = settings.value?.ready;

  return ready ? READINESS_KEYS[`${ready.invoice},${ready.receipt}`] ?? '' : '';
});
const fullyReady = computed(() => readinessKey.value === 'both');

// Read-only facts as label/value pairs rather than a `·`-joined sentence: the
// house `info-field` renders them the way patient-profile does. Split in two so
// each half stays under the complexity budget.
const companyFacts = computed(() => {
  const company = settings.value?.company;
  if (!company) return [];

  return [
    { icon: 'mdi-domain', label: t('taxSettings.fields.company'), value: company.name },
    {
      icon: 'mdi-card-account-details-outline',
      label: company.taxIdLabel ?? '',
      value: company.taxId ?? '—',
    },
  ];
});

const accountFacts = computed(() => {
  const current = account.value;
  if (!current?.connected) return [];
  const registeredName = current.registeredName;
  const certificateKey = current.certificate ? 'certificatePresent' : 'certificateAbsent';
  const name = {
    icon: 'mdi-office-building-outline',
    label: t('taxSettings.fields.registeredName'),
    value: registeredName ?? '',
  };

  return [
    ...(registeredName ? [name] : []),
    {
      icon: 'mdi-certificate-outline',
      label: t('taxSettings.fields.certificate'),
      value: t(`taxSettings.${certificateKey}`),
    },
  ];
});

const facts = computed(() => [...companyFacts.value, ...accountFacts.value]);

// Nobody should have to wonder whether a switch they flipped is in force: the
// card saves on demand, so it says plainly when something is still unsaved.
const CHOICE_KEYS = ['taxCategory', 'pricesIncludeTax', 'documentKind'] as const;
const isDirty = computed(() => {
  const saved = settings.value?.profile;

  return !!saved && CHOICE_KEYS.some((key) => saved[key] !== profileForm.value[key]);
});

const showEnrolment = ref(false);
const enrolmentError = ref('');
// Nothing can be looked up or enrolled for a company without a tax id.
const companyTaxIdMissing = computed(() => !!settings.value && !settings.value.company.taxId);
const validationSchema = object({
  authorityUserTaxId: string().required(t('taxSettings.enrol.required')),
  authorityPassword: string().required(t('taxSettings.enrol.required')),
  certificatePassword: string().required(t('taxSettings.enrol.required')),
});

const { handleSubmit, errors, defineField, resetForm } = useForm<EnrolmentForm>({
  validationSchema,
  initialValues: { authorityUserTaxId: '', authorityPassword: '', certificatePassword: '' },
});
const [authorityUserTaxId, authorityUserTaxIdAttrs] = defineField('authorityUserTaxId');
const [authorityPassword, authorityPasswordAttrs] = defineField('authorityPassword');
const [certificatePassword, certificatePasswordAttrs] = defineField('certificatePassword');

const { mutateAsync: enroll, isPending: enrolling } = useMutation({
  mutationFn: (form: EnrolmentForm) => taxSettingsApi.enroll(form),
  onSuccess: (fresh) => {
    replaceSettings(fresh);
    resetForm({
      values: {
        authorityUserTaxId: '', authorityPassword: '', certificatePassword: '',
      },
    });
    showEnrolment.value = false;
    notify(t('taxSettings.enrol.done'));
  },
  onError: (error) => {
    const code = apiErrorCode(error);
    const known = ['credentials_refused', 'not_enrolled', 'provider_unavailable'].includes(code ?? '');
    enrolmentError.value = known ?
      t(`taxSettings.enrol.errors.${camelize(code!)}`, { detail: apiErrorText(error, '') }) :
      apiErrorText(error, t('taxSettings.enrol.errors.unknown'));
  },
});

const submitEnrolment = handleSubmit(async (values) => {
  enrolmentError.value = '';
  await enroll(values).catch(() => undefined);
});

const { mutateAsync: refresh, isPending: refreshing } = useMutation({
  mutationFn: () => taxSettingsApi.refresh(),
  onSuccess: (fresh) => {
    replaceSettings(fresh);
    notify(t('taxSettings.refreshed'));
  },
  onError: (error) => report(error, 'taxSettings.refreshError'),
});
</script>

<template>
  <v-card v-if="isLoading || !settings">
    <v-card-text>
      <v-progress-circular
        indeterminate
        color="primary"
      />
    </v-card-text>
  </v-card>
  <template v-else>
    <!-- Two cards because there are two interaction models here: everything in
         the first acts the moment it is pressed, everything in the second waits
         for Guardar. Rendered as one column they were indistinguishable. -->
    <v-card class="mb-4">
      <!-- The state the card exists to report sits with the card's own title;
           MD3 asks for one word in the pill, and the readiness line below adds
           icon and colour so the state never rests on colour alone. -->
      <v-card-title class="d-flex align-center justify-space-between ga-3">
        <span>{{ t('taxSettings.connectionTitle') }}</span>
        <v-chip
          :color="connectionChip.color"
          size="small"
          data-test="connection-status"
        >
          {{ connectionChip.label }}
        </v-chip>
      </v-card-title>
      <v-card-subtitle class="text-wrap">
        {{ t('taxSettings.connectionSubtitle') }}
      </v-card-subtitle>
      <v-card-text>
        <p
          v-if="connected && readinessKey"
          class="text-body-2 mb-4"
          :class="fullyReady ? 'text-success' : 'text-warning'"
          data-test="readiness"
        >
          <v-icon
            :icon="fullyReady ? 'mdi-check-circle-outline' : 'mdi-alert-outline'"
            size="16"
            class="me-1"
          />
          {{ t(`taxSettings.readiness.${readinessKey}`) }}
        </p>

        <section-header
          icon="mdi-shield-check-outline"
          :title="t('taxSettings.identification')"
          class="mb-3"
        />
        <v-row class="mb-2">
          <v-col
            v-for="fact in facts"
            :key="fact.label"
            cols="12"
            sm="6"
            md="3"
          >
            <info-field
              :icon="fact.icon"
              :label="fact.label"
              :value="fact.value"
            />
          </v-col>
        </v-row>

        <v-alert
          v-if="webhookNotice"
          type="warning"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          {{ webhookNotice }}
        </v-alert>
        <v-alert
          v-if="companyTaxIdMissing"
          type="error"
          variant="tonal"
          density="compact"
          class="mb-3"
          data-test="tax-id-missing"
        >
          {{ t('taxSettings.companyTaxIdMissing') }}
        </v-alert>
        <!-- Re-sending the SII credentials is the rare action here, so it does
             not outshout Guardar in the card below. -->
        <div class="d-flex flex-wrap ga-2">
          <v-btn
            color="primary"
            variant="tonal"
            :disabled="companyTaxIdMissing"
            @click="showEnrolment = !showEnrolment"
          >
            {{ connected ? t('taxSettings.enrol.update') : t('taxSettings.enrol.connect') }}
          </v-btn>
          <v-btn
            v-if="connected"
            variant="text"
            :loading="refreshing"
            @click="refresh()"
          >
            {{ t('taxSettings.refresh') }}
          </v-btn>
        </div>

        <v-expand-transition>
          <form
            v-if="showEnrolment"
            class="mt-4"
            data-test="enrolment-form"
            @submit.prevent="submitEnrolment"
          >
            <p class="text-body-2 mb-3">
              {{ t('taxSettings.enrol.explanation') }}
            </p>
            <v-text-field
              v-model="authorityUserTaxId"
              v-bind="authorityUserTaxIdAttrs"
              :label="t('taxSettings.enrol.authorityUserTaxId')"
              :hint="t('taxSettings.enrol.authorityUserTaxIdHint')"
              :error-messages="errors.authorityUserTaxId"
              variant="outlined"
              density="compact"
              persistent-hint
              class="mb-4"
            />
            <v-text-field
              v-model="authorityPassword"
              v-bind="authorityPasswordAttrs"
              type="password"
              autocomplete="off"
              :label="t('taxSettings.enrol.authorityPassword')"
              :error-messages="errors.authorityPassword"
              variant="outlined"
              density="compact"
              class="mb-4"
            />
            <v-text-field
              v-model="certificatePassword"
              v-bind="certificatePasswordAttrs"
              type="password"
              autocomplete="off"
              :label="t('taxSettings.enrol.certificatePassword')"
              :hint="t('taxSettings.enrol.certificatePasswordHint')"
              :error-messages="errors.certificatePassword"
              variant="outlined"
              density="compact"
              persistent-hint
              class="mb-4"
            />
            <v-alert
              v-if="enrolmentError"
              type="error"
              variant="tonal"
              density="compact"
              class="mb-3"
            >
              {{ enrolmentError }}
            </v-alert>
            <v-btn
              type="submit"
              color="primary"
              :loading="enrolling"
            >
              {{ t('taxSettings.enrol.submit') }}
            </v-btn>
          </form>
        </v-expand-transition>
      </v-card-text>
    </v-card>

    <v-card>
      <v-card-title>{{ t('taxSettings.choices.title') }}</v-card-title>
      <v-card-subtitle class="text-wrap">
        {{ t('taxSettings.choices.choicesSubtitle') }}
      </v-card-subtitle>
      <v-card-text>
        <!-- Every setting follows the same rhythm: label and hint left, control
             right, stacked on a phone. Whether the company issues at all is
             Kuido's switch (administration), not one of these. -->
        <div>
          <section-header
            icon="mdi-file-document-edit-outline"
            :title="t('taxSettings.choices.rulesTitle')"
            class="mb-2"
          />

          <v-row
            align="center"
            class="mb-1"
          >
            <v-col
              cols="12"
              sm="7"
            >
              <div class="text-body-1 font-weight-medium">
                {{ t('taxSettings.choices.taxCategory') }}
              </div>
              <p class="text-caption text-medium-emphasis mb-0">
                {{ t('taxSettings.choices.taxCategoryHint') }}
              </p>
            </v-col>
            <v-col
              cols="12"
              sm="5"
              class="choice-control"
            >
              <v-select
                v-model="profileForm.taxCategory"
                :items="taxCategoryOptions"
                variant="outlined"
                density="compact"
                hide-details
              />
            </v-col>
          </v-row>

          <v-row
            align="center"
            class="mb-1"
          >
            <v-col
              cols="12"
              sm="7"
            >
              <div class="text-body-1 font-weight-medium">
                {{ t('taxSettings.choices.pricesIncludeTax') }}
              </div>
              <p class="text-caption text-medium-emphasis mb-0">
                {{ t('taxSettings.choices.pricesIncludeTaxHint') }}
              </p>
            </v-col>
            <v-col
              cols="12"
              sm="5"
              class="d-flex justify-sm-end"
            >
              <v-switch
                v-model="profileForm.pricesIncludeTax"
                color="primary"
                density="compact"
                hide-details
                class="flex-grow-0"
              />
            </v-col>
          </v-row>

          <v-row align="center">
            <v-col
              cols="12"
              sm="7"
            >
              <div class="text-body-1 font-weight-medium">
                {{ t('taxSettings.choices.documentKind') }}
              </div>
              <p class="text-caption text-medium-emphasis mb-0">
                {{ t('taxSettings.choices.documentKindHint') }}
              </p>
            </v-col>
            <v-col
              cols="12"
              sm="5"
              class="choice-control"
            >
              <v-radio-group
                v-model="profileForm.documentKind"
                inline
                hide-details
              >
                <v-radio
                  v-for="option in documentKindOptions"
                  :key="option.value"
                  :value="option.value"
                  :label="option.title"
                />
              </v-radio-group>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
      <v-card-actions class="px-4 pb-4">
        <v-btn
          color="primary"
          variant="flat"
          :disabled="!isDirty"
          :loading="saving"
          data-test="save-profile"
          @click="saveProfile(profileForm).catch(() => undefined)"
        >
          {{ t('taxSettings.choices.save') }}
        </v-btn>
        <span
          v-if="isDirty"
          class="text-caption text-medium-emphasis ml-3"
        >
          {{ t('taxSettings.unsavedChanges') }}
        </span>
      </v-card-actions>
    </v-card>
  </template>

  <v-snackbar
    v-model="successSnackbar"
    :timeout="4000"
    color="primary"
    rounded="pill"
  >
    {{ successMessage }}
  </v-snackbar>
  <v-snackbar
    v-model="errorSnackbar"
    :timeout="5000"
    color="error"
    rounded="pill"
  >
    {{ errorMessage }}
  </v-snackbar>
</template>

<style scoped>
/* A control wider than its own label reads as the more important half of the
   row, so the inputs stop growing well before the column does. */
.choice-control {
  max-width: 320px;
  margin-left: auto;
}
</style>
